import { useEffect, useState } from "react";
import { Clock, Loader2, Plus, Trash2 } from "lucide-react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const Row = ({ label, win, onChange, onRemove, canManage, testid }) => (
  <div className="flex items-center gap-2" data-testid={testid}>
    <span className="w-56 truncate text-sm text-slate-700 dark:text-slate-200">{label}</span>
    <Input type="time" value={win.start} disabled={!canManage} className="h-8 w-28 dark:[color-scheme:dark]"
      onChange={(e) => onChange({ ...win, start: e.target.value })} data-testid={`${testid}-start`} />
    <span className="text-slate-400">→</span>
    <Input type="time" value={win.end} disabled={!canManage} className="h-8 w-28 dark:[color-scheme:dark]"
      onChange={(e) => onChange({ ...win, end: e.target.value })} data-testid={`${testid}-end`} />
    {onRemove && canManage && (
      <button type="button" onClick={onRemove} data-testid={`${testid}-remove`}
        className="text-slate-300 transition-colors hover:text-rose-600 dark:text-slate-500">
        <Trash2 className="h-4 w-4" />
      </button>
    )}
  </div>
);

/** Productivity → Settings → Working window. Caps each person's counted day so a
 *  machine left on overnight stops reading as 23h 59m online. */
export function ProdWindowCard({ canManage }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addKind, setAddKind] = useState("teams");
  const [addName, setAddName] = useState("");

  const load = () => api.get("/productivity/window").then(({ data }) => setCfg(data))
    .catch(() => toast.error("Couldn't load the working window"));
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/productivity/window", {
        start: cfg.start, end: cfg.end, teams: cfg.teams, people: cfg.people,
        late_grace_minutes: Number(cfg.late_grace_minutes ?? 5),
      });
      setCfg(data);
      toast.success("Working window saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that");
      load();
    } finally { setSaving(false); }
  };

  const addOverride = () => {
    if (!addName) return;
    setCfg((c) => ({ ...c, [addKind]: { ...c[addKind], [addName]: { start: c.start, end: c.end } } }));
    setAddName("");
  };

  if (!cfg) return null;
  const peopleName = (k) => (cfg.people_options.find((p) => p.key === k) || {}).name || k;

  return (
    <Card className="p-4" data-testid="prod-window-card">
      <div className="mb-1 flex items-center gap-2 text-sm font-semibold text-slate-700 dark:text-slate-200">
        <Clock className="h-4 w-4" /> Working window
      </div>
      <p className="mb-3 text-xs text-slate-500 dark:text-slate-400">
        A day now counts the time somebody was genuinely at the machine — any block flagged as
        idle (10 minutes or more with no keyboard or mouse) is dropped first, so a machine left on
        overnight no longer reads as 23h 59m. On-call, evening and weekend work all still count.
        <br />
        <span className="text-emerald-700 dark:text-emerald-300">
          The monitoring tool reports each person&apos;s real first and last activity, so this window
          is no longer used as a {cfg.hours_per_day}h/day ceiling — it marks the expected hours, and
          anything worked outside it is flagged on the timecard as &quot;out&quot; rather than dropped.
        </span>
      </p>

      <div className="space-y-2">
        <Row label="Everyone (default)" win={{ start: cfg.start, end: cfg.end }} canManage={canManage}
          onChange={(w) => setCfg((c) => ({ ...c, start: w.start, end: w.end }))}
          testid="prod-window-default" />
        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 px-3 py-2 dark:border-slate-700"
             data-testid="prod-window-late-grace">
          <span className="text-xs font-medium text-slate-700 dark:text-slate-200">Late after</span>
          <input type="number" min="0" max="120" disabled={!canManage}
                 value={cfg.late_grace_minutes ?? 5}
                 data-testid="prod-window-late-grace-input"
                 onChange={(e) => setCfg((c) => ({ ...c, late_grace_minutes: e.target.value }))}
                 className="h-8 w-[70px] rounded-md border border-slate-200 bg-white px-2 text-xs dark:border-slate-700 dark:bg-slate-900" />
          <span className="text-xs text-slate-500 dark:text-slate-400">
            minutes past the scheduled start — used by the shift report and the daily email (times in MDT)
          </span>
        </div>
        {Object.entries(cfg.teams || {}).map(([team, win]) => (
          <Row key={team} label={`Team: ${team}`} win={win} canManage={canManage}
            onChange={(w) => setCfg((c) => ({ ...c, teams: { ...c.teams, [team]: w } }))}
            onRemove={() => setCfg((c) => {
              const t = { ...c.teams }; delete t[team]; return { ...c, teams: t };
            })}
            testid={`prod-window-team-${team.replace(/\s+/g, "-").toLowerCase()}`} />
        ))}
        {Object.entries(cfg.people || {}).map(([key, win]) => (
          <Row key={key} label={peopleName(key)} win={win} canManage={canManage}
            onChange={(w) => setCfg((c) => ({ ...c, people: { ...c.people, [key]: w } }))}
            onRemove={() => setCfg((c) => {
              const p = { ...c.people }; delete p[key]; return { ...c, people: p };
            })}
            testid={`prod-window-person-${key}`} />
        ))}
      </div>

      {canManage && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <select value={addKind} onChange={(e) => { setAddKind(e.target.value); setAddName(""); }}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            data-testid="prod-window-add-kind">
            <option value="teams">Different hours for a team</option>
            <option value="people">Different hours for one person</option>
          </select>
          <select value={addName} onChange={(e) => setAddName(e.target.value)}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            data-testid="prod-window-add-name">
            <option value="">Choose…</option>
            {addKind === "teams"
              ? (cfg.team_options || []).filter((t) => !(cfg.teams || {})[t]).map((t) => <option key={t} value={t}>{t}</option>)
              : (cfg.people_options || []).filter((p) => !(cfg.people || {})[p.key]).map((p) => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={addOverride} disabled={!addName}
            data-testid="prod-window-add-btn">
            <Plus className="mr-1 h-4 w-4" /> Add
          </Button>
          <Button size="sm" onClick={save} disabled={saving} data-testid="prod-window-save">
            {saving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Save window
          </Button>
        </div>
      )}
    </Card>
  );
}
